<template>
    <div>
        <h1>Ustawienia</h1>

        <div class="settings-section">
            <h2>Powiadomienia</h2>
            <form @submit.prevent="saveSettings">
                <div class="form-group">
                    <label for="email">Email do wysyłania powiadomień:</label>
                    <input id="email" type="email" v-model="email" required />
                </div>

                <button class="save-button" type="submit" :disabled="settingsStore.loading">
                    {{ settingsStore.loading ? 'Zapisywanie...' : 'Zapisz' }}
                </button>
            </form>

            <p v-if="settingsStore.error" class="error-message">
                Wystąpił błąd: {{ settingsStore.error.message || settingsStore.error }}
            </p>
        </div>

        <div class="settings-section">
            <h2>Zmiana hasła</h2>
            <form @submit.prevent="changePassword">
                <div class="form-group">
                    <label for="current_password">Obecne hasło</label>
                    <input id="current_password" type="password" v-model="passwordForm.current_password" required />
                </div>
                <div class="form-group">
                    <label for="password">Nowe hasło</label>
                    <input id="password" type="password" v-model="passwordForm.password" required />
                </div>
                <div class="form-group">
                    <label for="password_confirmation">Potwierdź nowe hasło</label>
                    <input id="password_confirmation" type="password" v-model="passwordForm.password_confirmation" required />
                </div>

                <div v-if="passwordMessage" class="success-message">{{ passwordMessage }}</div>
                <div v-if="passwordError" class="error-message">{{ passwordError }}</div>

                <button class="save-button" type="submit" :disabled="passwordLoading">
                    {{ passwordLoading ? 'Zmienianie...' : 'Zmień hasło' }}
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useSettingsStore } from '@/stores/settingsStore'
import api from '@/services/apiService'

export default {
    setup() {
        const settingsStore = useSettingsStore()
        const email = ref('')

        const passwordForm = reactive({
            current_password: '',
            password: '',
            password_confirmation: ''
        })
        const passwordLoading = ref(false)
        const passwordMessage = ref(null)
        const passwordError = ref(null)

        onMounted(async () => {
            await settingsStore.fetchSettings()
            if (settingsStore.settings) {
                email.value = settingsStore.settings.email
            }
        })

        const saveSettings = async () => {
            await settingsStore.updateSettings({ email: email.value })
            if (settingsStore.settings) {
                email.value = settingsStore.settings.email
            }
        }

        const changePassword = async () => {
            passwordLoading.value = true
            passwordMessage.value = null
            passwordError.value = null

            if (passwordForm.password.length < 8) {
                passwordError.value = 'Hasło musi mieć co najmniej 8 znaków.'
                passwordLoading.value = false
                return
            }
            if (!/[^A-Za-z0-9]/.test(passwordForm.password)) {
                passwordError.value = 'Hasło musi zawierać co najmniej jeden znak specjalny (np. !@#$%).'
                passwordLoading.value = false
                return
            }
            if (passwordForm.password !== passwordForm.password_confirmation) {
                passwordError.value = 'Hasła nie są identyczne.'
                passwordLoading.value = false
                return
            }

            try {
                await api.auth.updatePassword(passwordForm)
                passwordMessage.value = 'Hasło zostało zmienione pomyślnie.'
                passwordForm.current_password = ''
                passwordForm.password = ''
                passwordForm.password_confirmation = ''
            } catch (err) {
                console.error('Błąd zmiany hasła:', err)
                const msg = err.response?.data?.message
                if (msg === 'The provided password is incorrect.') {
                    passwordError.value = 'Obecne hasło jest nieprawidłowe.'
                } else {
                    passwordError.value = 'Wystąpił błąd podczas zmiany hasła.'
                }
            } finally {
                passwordLoading.value = false
            }
        }

        return { 
            email, 
            settingsStore, 
            saveSettings,
            passwordForm,
            passwordLoading,
            passwordMessage,
            passwordError,
            changePassword
        }
    }
}
</script>

<style lang="scss" scoped>
.settings-section {
    background-color: $color-white;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 0 10px rgba(0,0,0,0.05);
    margin-bottom: 2rem;

    h2 {
        margin-bottom: 1.5rem;
        font-size: 1.25rem;
        color: $color-primary-dark;
        border-bottom: 1px solid $color-grey-light;
        padding-bottom: 0.5rem;
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 1rem;

    label {
        font-weight: 500;
        font-size: 1rem;
    }

    input {
        font-family: 'Inter', sans-serif;
        padding: 10px;
        border: 2px solid $color-grey;
        border-radius: 8px;
        font-size: 1rem;
        width: 100%;
        max-width: 400px;
        transition: border 0.3s ease;

        &:focus {
            border-color: $color-primary;
            outline: none;
        }
    }
}

button {
    padding: 10px 24px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.save-button {
    background-color: $color-primary;
    color: $color-white;

    &:hover {
        background-color: $color-primary-dark;
    }

    &:disabled {
        background-color: lighten($color-primary, 20%);
        cursor: not-allowed;
    }
}

.success-message {
    color: #155724;
    background-color: #d4edda;
    padding: 10px;
    border-radius: 6px;
    margin-bottom: 1rem;
    max-width: 400px;
}

.error-message {
    color: #721c24;
    background-color: #f8d7da;
    padding: 10px;
    border-radius: 6px;
    margin-bottom: 1rem;
    max-width: 400px;
}
</style>